const STORAGE_KEY = 'utilization_studio_db_v1';

const defaultDB = () => ({
  templates: [],
  dailyEvents: [],
  dailyFacts: [],
  computeRuns: [],
  flightSchedules: [],
  flightBayMappings: [],
  flightImports: [],
  updatedAt: null,
});

const safeClone = (value) => JSON.parse(JSON.stringify(value));

const loadDB = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultDB();
    const parsed = JSON.parse(raw);
    return {
      ...defaultDB(),
      ...parsed,
      templates: Array.isArray(parsed.templates) ? parsed.templates : [],
      dailyEvents: Array.isArray(parsed.dailyEvents) ? parsed.dailyEvents : [],
      dailyFacts: Array.isArray(parsed.dailyFacts) ? parsed.dailyFacts : [],
      computeRuns: Array.isArray(parsed.computeRuns) ? parsed.computeRuns : [],
      flightSchedules: Array.isArray(parsed.flightSchedules) ? parsed.flightSchedules : [],
      flightBayMappings: Array.isArray(parsed.flightBayMappings) ? parsed.flightBayMappings : [],
      flightImports: Array.isArray(parsed.flightImports) ? parsed.flightImports : [],
    };
  } catch (error) {
    console.error('[utilizationDB] load failed:', error);
    return defaultDB();
  }
};

const saveDB = (db) => {
  const next = {
    ...db,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

export const listUtilizationTemplates = () => {
  const db = loadDB();
  return safeClone(db.templates);
};

export const getUtilizationTemplate = (templateId) => {
  const db = loadDB();
  const found = db.templates.find((t) => t.id === templateId) || null;
  return safeClone(found);
};

export const upsertUtilizationTemplate = (template) => {
  const db = loadDB();
  const now = new Date().toISOString();
  const normalized = {
    ...template,
    createdAt: template.createdAt || now,
    updatedAt: now,
  };

  const idx = db.templates.findIndex((t) => t.id === template.id);
  if (idx >= 0) {
    db.templates[idx] = normalized;
  } else {
    db.templates.push(normalized);
  }

  saveDB(db);
  return safeClone(normalized);
};

export const deleteUtilizationTemplate = (templateId) => {
  const db = loadDB();
  db.templates = db.templates.filter((t) => t.id !== templateId);
  db.dailyEvents = db.dailyEvents.filter((e) => e.templateId !== templateId);
  db.dailyFacts = db.dailyFacts.filter((f) => f.templateId !== templateId);
  db.flightSchedules = db.flightSchedules.filter((f) => f.templateId !== templateId);
  db.flightBayMappings = db.flightBayMappings.filter((m) => m.templateId !== templateId);
  db.flightImports = db.flightImports.filter((i) => i.templateId !== templateId);
  saveDB(db);
};

export const saveUtilizationDayData = ({ templateId, dateKey, events, facts, runMeta }) => {
  const db = loadDB();
  db.dailyEvents = db.dailyEvents.filter((e) => !(e.templateId === templateId && e.date === dateKey));
  db.dailyFacts = db.dailyFacts.filter((f) => !(f.templateId === templateId && f.date === dateKey));

  db.dailyEvents.push(...events);
  db.dailyFacts.push(...facts);
  db.computeRuns.push({
    templateId,
    date: dateKey,
    at: new Date().toISOString(),
    ...runMeta,
  });

  saveDB(db);
};

export const listUtilizationEvents = ({ templateId, date, assetId }) => {
  const db = loadDB();
  return safeClone(
    db.dailyEvents
      .filter((event) => (!templateId || event.templateId === templateId))
      .filter((event) => (!date || event.date === date))
      .filter((event) => (!assetId || event.assetId === assetId))
      .sort((a, b) => a.startTs - b.startTs)
  );
};

export const listUtilizationFacts = ({ templateId, dateFrom, dateTo, assetIds }) => {
  const db = loadDB();
  const fromTs = dateFrom ? new Date(dateFrom).getTime() : null;
  const toTs = dateTo ? new Date(dateTo).getTime() : null;

  return safeClone(
    db.dailyFacts.filter((fact) => {
      if (templateId && fact.templateId !== templateId) return false;
      if (assetIds && assetIds.length > 0 && !assetIds.includes(fact.assetId)) return false;

      if (fromTs !== null || toTs !== null) {
        const factTs = new Date(`${fact.date}T00:00:00.000Z`).getTime();
        if (fromTs !== null && factTs < fromTs) return false;
        if (toTs !== null && factTs > toTs) return false;
      }

      return true;
    })
  );
};

export const saveFlightImportBatch = ({ templateId, batch, flights, replaceLocalDates = [] }) => {
  const db = loadDB();
  const dateSet = new Set((replaceLocalDates || []).filter(Boolean));

  if (dateSet.size > 0) {
    db.flightSchedules = db.flightSchedules.filter((row) => {
      if (row.templateId !== templateId) return true;
      return !dateSet.has(row.localDate);
    });
  }

  db.flightSchedules.push(...(flights || []));

  if (batch) {
    db.flightImports.push({
      ...batch,
      templateId,
      importedAt: batch.importedAt || new Date().toISOString(),
    });
  }

  saveDB(db);
  return {
    savedRows: (flights || []).length,
    replacedDates: Array.from(dateSet),
    batch: safeClone(batch || null),
  };
};

export const listFlightSchedules = ({ templateId, dateFrom, dateTo }) => {
  const db = loadDB();
  return safeClone(
    db.flightSchedules
      .filter((row) => (!templateId || row.templateId === templateId))
      .filter((row) => (!dateFrom || (row.localDate && row.localDate >= dateFrom)))
      .filter((row) => (!dateTo || (row.localDate && row.localDate <= dateTo)))
      .sort((a, b) => {
        if (a.localDate !== b.localDate) return `${a.localDate}`.localeCompare(`${b.localDate}`);
        return Number(a.ataTsUtc || 0) - Number(b.ataTsUtc || 0);
      })
  );
};

export const listFlightImportBatches = ({ templateId }) => {
  const db = loadDB();
  return safeClone(
    db.flightImports
      .filter((row) => (!templateId || row.templateId === templateId))
      .sort((a, b) => `${b.importedAt || ''}`.localeCompare(`${a.importedAt || ''}`))
  );
};

export const upsertFlightBayMappings = ({ templateId, mappings }) => {
  const db = loadDB();
  const now = new Date().toISOString();
  const input = Array.isArray(mappings) ? mappings : [];

  input.forEach((entry) => {
    const bayKey = `${entry?.bayKey || ''}`.trim();
    if (!bayKey) return;

    const idx = db.flightBayMappings.findIndex(
      (m) => m.templateId === templateId && `${m.bayKey || ''}`.trim().toUpperCase() === bayKey.toUpperCase()
    );

    const normalized = {
      id: entry?.id || `bay_map_${templateId}_${bayKey}`,
      templateId,
      bayKey,
      zoneId: entry?.zoneId || null,
      method: entry?.method || (entry?.zoneId ? 'manual' : 'auto'),
      confidence: entry?.confidence || (entry?.zoneId ? 'high' : 'low'),
      notes: entry?.notes || '',
      updatedAt: now,
    };

    if (idx >= 0) {
      db.flightBayMappings[idx] = {
        ...db.flightBayMappings[idx],
        ...normalized,
      };
    } else {
      db.flightBayMappings.push(normalized);
    }
  });

  saveDB(db);
  return safeClone(db.flightBayMappings.filter((m) => m.templateId === templateId));
};

export const listFlightBayMappings = ({ templateId }) => {
  const db = loadDB();
  return safeClone(
    db.flightBayMappings
      .filter((entry) => (!templateId || entry.templateId === templateId))
      .sort((a, b) => `${a.bayKey || ''}`.localeCompare(`${b.bayKey || ''}`))
  );
};

export const clearFlightDataForTemplate = (templateId) => {
  const db = loadDB();
  db.flightSchedules = db.flightSchedules.filter((row) => row.templateId !== templateId);
  db.flightBayMappings = db.flightBayMappings.filter((row) => row.templateId !== templateId);
  db.flightImports = db.flightImports.filter((row) => row.templateId !== templateId);
  saveDB(db);
};

export const clearUtilizationDB = () => {
  localStorage.removeItem(STORAGE_KEY);
};
